import React, { useState } from 'react';
import { Dropdown, Empty } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { getDashboardWidgetQuery } from '@src/util/query/dashboardLayout.query';
import { formatCurrencyTotals, formatMoney, formatMoneyCompact } from '@src/util/currency/currency';
import { MetricTile, PanelCard, useDashboard } from './widgetShell';
import { RangeBadge, rangeVariables } from './widgetRange';
import { scheduleColumns } from './dashboardDetailHost';

// Money is never added across currencies (see wfc-server dashboardWidget.service): every total is
// { GBP: 120, EUR: 40 }, shown default currency first.
const FINANCE_RANGES = ['today', 'weekly', 'monthly', 'yearly', 'custom', 'all'];
const BA = '/user/businessAnalysis';

const useFinance = (widget, settings, fallback = 'monthly') => {
  const { data, loading } = useQuery(getDashboardWidgetQuery, {
    variables: { widget, ...rangeVariables(settings, fallback) },
    fetchPolicy: 'cache-and-network',
  });
  return { response: data?.getDashboardWidget?.response, loading: loading && !data };
};

const money = (totals, first) => formatCurrencyTotals(totals, { compact: true, first });

const PAY_BILL_TILES = {
  bill: { label: 'Total bill', value: (r) => money(r.bill, r.defaultCurrency) },
  pay: { label: 'Total pay', value: (r) => money(r.pay, r.defaultCurrency) },
  margin: {
    label: 'Margin',
    value: (r) => money(r.margin, r.defaultCurrency),
    sub: (r) => Object.entries(r.marginPercent || {}).map(([code, pct]) => `${pct}%${Object.keys(r.marginPercent).length > 1 ? ` (${code})` : ''}`).join(' · '),
  },
};

/** One pay & bill figure (bill, pay or margin) for the period's duties */
export const PayBillFigureWidget = ({ metric, settings, onSettings }) => {
  const navigate = useNavigate();
  const { response, loading } = useFinance('payBill', settings);
  const meta = PAY_BILL_TILES[metric];
  const negative = metric === 'margin' && Object.values(response?.margin || {}).some((v) => v < 0);
  return (
    <MetricTile
      label={meta.label}
      value={loading || !response ? undefined : meta.value(response)}
      loading={loading}
      alert={negative}
      badge={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={FINANCE_RANGES} />}
      onClick={() => navigate(BA)}
      hint="Open Business Analysis"
      sub={response ? (meta.sub?.(response) || `${response.calculated} of ${response.duties} duties calculated`) : null}
    />
  );
};

/** Bill, pay and margin side by side, with how many duties are still to be calculated */
export const PayBillSummaryWidget = ({ settings, onSettings }) => {
  const navigate = useNavigate();
  const { response, loading } = useFinance('payBill', settings);
  const first = response?.defaultCurrency;
  return (
    <PanelCard title="Pay & bill" extra={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={FINANCE_RANGES} />}>
      {loading || !response ? <div className="dw-empty">Loading…</div> : (
        <div className="dw-finance" onClick={() => navigate(BA)} data-hint="Open Business Analysis">
          <div className="dw-finance-cell"><span>Bill</span><b data-metric-value>{formatCurrencyTotals(response.bill, { first })}</b></div>
          <div className="dw-finance-cell"><span>Pay</span><b>{formatCurrencyTotals(response.pay, { first })}</b></div>
          <div className={`dw-finance-cell${Object.values(response.margin || {}).some((v) => v < 0) ? ' negative' : ''}`}>
            <span>Margin</span>
            <b>{formatCurrencyTotals(response.margin, { first })}</b>
            <em>{Object.entries(response.marginPercent || {}).map(([code, pct]) => `${pct}%${Object.keys(response.marginPercent).length > 1 ? ` ${code}` : ''}`).join(' · ')}</em>
          </div>
          <div className="dw-finance-note">
            {response.calculated} of {response.duties} duties calculated
            {response.completedNotCalculated > 0 && <> · <b>{response.completedNotCalculated}</b> completed, not calculated yet</>}
          </div>
        </div>
      )}
    </PanelCard>
  );
};

/** Completed duties that aren't on an invoice yet, and what they'd bill */
export const UninvoicedWidget = ({ settings, onSettings }) => {
  const { openDetail } = useDashboard();
  const { response, loading } = useFinance('uninvoiced', settings);
  return (
    <MetricTile
      label="Not invoiced yet"
      value={loading || !response ? undefined : response.count}
      loading={loading}
      alert={response?.count > 0}
      badge={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={FINANCE_RANGES} />}
      onClick={() => openDetail('schedule', { title: 'Completed duties not invoiced', columns: scheduleColumns, emptyText: 'Every completed duty in this period is invoiced.', variables: { ids: response?.ids || [] } })}
      sub={response ? `${money(response.bill, response.defaultCurrency)} to bill${response.notCalculated ? ` · ${response.notCalculated} not calculated` : ''}` : null}
    />
  );
};

const STATUS_LABEL = { draft: 'Draft', sent: 'Sent', paid: 'Paid', void: 'Void', cancelled: 'Cancelled' };

/** Invoices raised in the period, by status */
export const InvoicesWidget = ({ settings, onSettings }) => {
  const navigate = useNavigate();
  const { response, loading } = useFinance('invoices', settings);
  const statuses = Object.entries(response?.byStatus || {});
  return (
    <MetricTile
      label="Invoices"
      value={loading || !response ? undefined : response.count}
      loading={loading}
      badge={<RangeBadge settings={settings} onChange={onSettings} fallback="monthly" options={FINANCE_RANGES} />}
      onClick={() => navigate(BA)}
      hint="Open Business Analysis"
      sub={statuses.length ? statuses.map(([status, s]) => `${s.count} ${(STATUS_LABEL[status] || status).toLowerCase()} ${money(s.totals, response.defaultCurrency)}`).join(' · ') : (response ? 'None in this period' : null)}
    />
  );
};

/** Pay against bill over time - per day for short periods, per month for longer ones */
export const PayBillTrendWidget = ({ settings, onSettings }) => {
  const { response, loading } = useFinance('payBillTrend', settings, 'yearly');
  const [picked, setPicked] = useState(null);
  const currencies = response?.currencies || [];
  const currency = currencies.includes(picked) ? picked : currencies[0];
  const points = (response?.points || []).map((p) => ({ label: p.label, Bill: p.bill?.[currency] || 0, Pay: p.pay?.[currency] || 0 }));

  const extra = (
    <span className="dw-extra">
      {currencies.length > 1 && (
        <span className="no-drag" onPointerDown={(e) => e.stopPropagation()}>
          <Dropdown trigger={['click']} menu={{ items: currencies.map((code) => ({ key: code, label: code })), selectedKeys: [currency], onClick: ({ key }) => setPicked(key) }}>
            <span className="metric-badge clickable">{currency} <DownOutlined style={{ fontSize: '10px' }} /></span>
          </Dropdown>
        </span>
      )}
      <RangeBadge settings={settings} onChange={onSettings} fallback="yearly" options={['weekly', 'monthly', 'yearly', 'custom']} />
    </span>
  );

  return (
    <PanelCard title="Pay vs bill" extra={extra}>
      {points.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
            <XAxis dataKey="label" tick={{ fill: '#8c8c8c', fontSize: 11 }} />
            <YAxis tick={{ fill: '#8c8c8c', fontSize: 11 }} tickFormatter={(v) => formatMoneyCompact(v, currency)} width={60} />
            <Tooltip formatter={(value, name) => [formatMoney(value, currency), name]} contentStyle={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Bill" fill="#126E7D" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Pay" fill="#fb8c5f" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="dw-empty"><Empty description={loading ? 'Loading…' : 'No calculated pay or bill in this period.'} image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>
      )}
    </PanelCard>
  );
};
